import React, { useState, useEffect } from 'react';
import { ChevronLeft, Calendar, Clock, Plus, Send, Info, X } from 'lucide-react';
import { getAvailableSchedulesForRequest, createOnDemandRequest } from '../services/onDemandService';
import GlobalLoader from './GlobalLoader';
import Swal from 'sweetalert2';
import DoctorSummaryCard from './DoctorSummaryCard';
import { getDurationMinutes, getExtraConsultationPrice } from '../utils/checkoutHelpers';

/**
 * RequestOndemandPage - SOS Consultation Proposal Flow.
 * Allows users to propose up to 3 dates/times for an on-demand consultation.
 */
const RequestOndemandPage = ({ 
  doctor, 
  specialty, 
  loggedInUser, 
  onBack, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingSlots, setFetchingSlots] = useState(false);
  const [selectedDurationId, setSelectedDurationId] = useState(null);
  const [reason, setReason] = useState('');
  
  // Proposals state: [{ date: 'YYYY-MM-DD', slots: [], selectedSlotId: null }]
  const [proposals, setProposals] = useState([
    { id: Date.now(), date: '', slots: [], selectedSlotId: null }
  ]);

  // Initial setup: set default duration
  useEffect(() => {
    const extraDurations = specialty?.extra_durations || [];
    if (extraDurations.length > 0) {
      setSelectedDurationId(extraDurations[0].id);
    } else if (specialty?.extraDurationId) {
      setSelectedDurationId(specialty.extraDurationId);
    } else {
      setSelectedDurationId(specialty?.durationId || 109);
    }
  }, [specialty]);

  // Helper: check if user has active membership
  const hasActiveMembership = () => {
    if (!loggedInUser) return false;
    return (
      loggedInUser.membership?.state === 'active' ||
      loggedInUser.membership_active === 'active' ||
      loggedInUser.membership_active === true
    );
  };

  // Helper: get price for selected duration.
  // price_extra_consultations está keyado por MINUTOS (id == minutos), no por
  // el id del registro de duración. Convertimos el duration_id a minutos y
  // buscamos el costo (con/sin membresía).
  const getPrice = () => {
    const minutes = getDurationMinutes(specialty, selectedDurationId);
    const cost = getExtraConsultationPrice(specialty, minutes, hasActiveMembership());
    return cost != null ? `$${cost}` : '$450';
  };

  // Add a new proposal row (max 3)
  const addProposal = () => {
    if (proposals.length < 3) {
      setProposals([...proposals, { id: Date.now(), date: '', slots: [], selectedSlotId: null }]);
    }
  };

  // Remove a proposal row
  const removeProposal = (id) => {
    if (proposals.length > 1) {
      setProposals(proposals.filter(p => p.id !== id));
    }
  };

  // Handle date change for a specific proposal
  const handleDateChange = async (id, date) => {
    const updatedProposals = proposals.map(p => {
      if (p.id === id) {
        return { ...p, date: date, slots: [], selectedSlotId: null };
      }
      return p;
    });
    setProposals(updatedProposals);

    if (date && selectedDurationId && doctor?.id) {
      setFetchingSlots(true);
      try {
        const result = await getAvailableSchedulesForRequest(
          loggedInUser?.authToken || 'public',
          loggedInUser?.daisUserId || loggedInUser?.userId || 0,
          doctor.id,
          selectedDurationId,
          date
        );
        
        if (result.success) {
          setProposals(prev => prev.map(p => 
            p.id === id ? { ...p, slots: result.slots } : p
          ));
        } else {
          console.warn('[RequestOndemandPage] No slots found for date:', date);
        }
      } catch (err) {
        console.error('[RequestOndemandPage] Error fetching slots:', err);
      } finally {
        setFetchingSlots(false);
      }
    }
  };

  // Handle slot selection
  const handleSlotSelect = (proposalId, slotId) => {
    setProposals(proposals.map(p => {
      if (p.id === proposalId) {
        return { ...p, selectedSlotId: p.selectedSlotId === slotId ? null : slotId };
      }
      return p;
    }));
  };

  // Validate and submit
  const handleSubmit = async () => {
    const selectedScheduleIds = proposals
      .map(p => p.selectedSlotId)
      .filter(id => id !== null);

    if (selectedScheduleIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Por favor selecciona al menos un horario propuesto.',
        confirmButtonColor: '#786ED9'
      });
      return;
    }

    if (!reason.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Motivo requerido',
        text: 'Por favor ingresa el motivo de tu consulta.',
        confirmButtonColor: '#786ED9'
      });
      return;
    }

    // Confirmation Modal
    const confirmResult = await Swal.fire({
      title: 'Confirmar solicitud SOS',
      html: `
        <div class="text-left text-sm space-y-2">
          <p>La propuesta de horarios SOS aplica sólo para consultas on-demand (extra).</p>
          <p><strong>Precio:</strong> ${getPrice()}</p>
          ${hasActiveMembership() ? '<p class="text-mint-deep font-semibold">Precio preferencial aplicado por membresía activa.</p>' : ''}
          <p class="text-gray-500 italic mt-4">Presiona en continuar para enviar tu solicitud a la doctora.</p>
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Continuar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#786ED9',
      cancelButtonColor: '#9E9E9E',
      reverseButtons: true
    });

    if (confirmResult.isConfirmed) {
      setLoading(true);
      try {
        const result = await createOnDemandRequest(
          loggedInUser?.authToken,
          loggedInUser?.daisUserId || loggedInUser?.userId,
          doctor.id,
          selectedDurationId,
          selectedScheduleIds,
          reason
        );

        if (result.success) {
          Swal.fire({
            icon: 'success',
            title: '¡Solicitud enviada!',
            text: 'Tu solicitud ha sido enviada correctamente. La doctora revisará tus horarios propuestos.',
            confirmButtonColor: '#786ED9'
          }).then(() => {
            onSuccess(result);
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: result.message || 'Hubo un problema al crear tu solicitud.',
            confirmButtonColor: '#786ED9'
          });
        }
      } catch (err) {
        console.error('[RequestOndemandPage] Submit error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error de conexión',
          text: 'No se pudo contactar con el servidor.',
          confirmButtonColor: '#786ED9'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const totalSelected = proposals.filter(p => p.selectedSlotId !== null).length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 min-h-screen bg-warm-canvas">
      {loading && <GlobalLoader title="Enviando solicitud" subtitle="Estamos procesando tu propuesta SOS..." />}

      <button 
        onClick={onBack}
        className="press-scale mb-6 text-daisies flex items-center gap-2 hover:gap-3 transition-all font-medium"
      >
        <ChevronLeft size={20} />
        Volver
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Doctor Info & Duration */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-card shadow-soft p-1">
             <DoctorSummaryCard doctor={doctor} defaultOpen={true} />
          </div>

          <div className="bg-white rounded-card shadow-soft p-5">
            <h3 className="text-sm font-bold text-text-primary mb-4 flex items-center gap-2">
              <Clock size={18} className="text-daisies" />
              Duración de consulta
            </h3>
            
            <div className="space-y-3">
              {(specialty?.extra_durations || []).map((dur) => (
                <button
                  key={dur.id}
                  onClick={() => setSelectedDurationId(dur.id)}
                  className={`w-full p-4 rounded-xl transition-all text-left flex items-center justify-between ${
                    selectedDurationId === dur.id
                      ? 'bg-daisies-50 text-daisies ring-2 ring-brand'
                      : 'bg-warm-canvas text-text-secondary hover:bg-daisies-50/50'
                  }`}
                >
                  <span className="font-semibold">{dur.duration} minutos</span>
                  {selectedDurationId === dur.id && <div className="w-2 h-2 rounded-full bg-daisies" />}
                </button>
              ))}
              
              {(!specialty?.extra_durations || specialty.extra_durations.length === 0) && (
                 <div className="p-4 rounded-xl bg-daisies-50 text-daisies font-semibold">
                   {specialty?.durationId ? 'Duración estándar' : 'Cargando duración...'}
                 </div>
              )}
            </div>

            <div className="mt-6 p-4 rounded-xl bg-brand-tint">
              <p className="text-xs text-text-secondary mb-1">Precio por consulta extra:</p>
              <p className="text-2xl font-bold text-daisies">{getPrice()}</p>
              {hasActiveMembership() && (
                <p className="text-[10px] text-mint-deep font-medium mt-1">✓ Incluye descuento por membresía</p>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Proposals & Reason */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-card shadow-soft p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-bold text-text-primary tracking-tight">Proponer horarios</h2>
                <p className="text-sm text-text-secondary">Selecciona hasta 3 horarios diferentes</p>
              </div>
              <div className="bg-daisies-50 text-daisies px-3 py-1 rounded-full text-xs font-bold">
                {totalSelected} / 3 seleccionados
              </div>
            </div>

            <div className="space-y-8">
              {proposals.map((proposal, index) => (
                <div key={proposal.id} className="relative p-5 rounded-2xl bg-warm-canvas animate-fadeIn">
                  {proposals.length > 1 && (
                    <button 
                      onClick={() => removeProposal(proposal.id)}
                      className="absolute -top-2 -right-2 p-1.5 bg-white shadow-elevated rounded-full text-red-400 hover:text-red-600 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  )}

                  <div className="flex flex-col md:flex-row gap-4 mb-4">
                    <div className="flex-1">
                      <label className="block text-xs font-bold text-text-secondary uppercase tracking-wider mb-2">
                        Fecha propuesta {index + 1}
                      </label>
                      <div className="relative">
                        <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-daisies pointer-events-none" />
                        <input 
                          type="date"
                          value={proposal.date}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => handleDateChange(proposal.id, e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-brand transition-all text-sm font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {proposal.date && (
                    <div>
                      <label className="block text-xs font-bold text-text-secondary uppercase tracking-wider mb-3">
                        Horarios disponibles
                      </label>
                      
                      {fetchingSlots ? (
                        <div className="flex items-center gap-2 text-daisies animate-gentlePulse">
                          <div className="w-4 h-4 border-2 border-daisies border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs font-medium">Buscando slots...</span>
                        </div>
                      ) : proposal.slots.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {proposal.slots.map((slot) => (
                            <button
                              key={slot.id}
                              onClick={() => handleSlotSelect(proposal.id, slot.id)}
                              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                                proposal.selectedSlotId === slot.id
                                  ? 'bg-daisies text-white ring-2 ring-brand ring-offset-1'
                                  : 'bg-white text-text-primary hover:bg-daisies-50'
                              }`}
                            >
                              {slot.init}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-red-400 italic">No hay horarios disponibles para esta fecha.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {proposals.length < 3 && (
                <button
                  onClick={addProposal}
                  className="w-full py-4 border-2 border-dashed border-brand/30 rounded-2xl text-daisies font-bold flex items-center justify-center gap-2 hover:bg-daisies-50 transition-all group"
                >
                  <Plus size={20} className="group-hover:scale-110 transition-transform" />
                  Añadir otra fecha propuesta
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-card shadow-soft p-6">
            <h3 className="text-sm font-bold text-text-primary mb-4 flex items-center gap-2">
              <Info size={18} className="text-daisies" />
              Motivo de consulta
            </h3>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Cuéntanos un poco sobre el motivo de tu consulta..."
              className="w-full p-4 bg-warm-canvas rounded-xl focus:outline-none focus:ring-2 focus:ring-brand transition-all text-sm min-h-[120px] resize-none"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={totalSelected === 0 || !reason.trim() || loading}
            className={`w-full py-5 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${
              totalSelected > 0 && reason.trim() && !loading
                ? 'bg-daisies text-white shadow-elevated hover:bg-daisies-hover hover:scale-[1.01]'
                : 'bg-brand-tint text-text-mute cursor-not-allowed'
            }`}
          >
            <Send size={20} />
            Enviar propuesta SOS
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestOndemandPage;
